import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, Loader2, CheckCircle2, AlertTriangle, XCircle, HelpCircle, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Button } from '../components/ui/button';

const STATUS = {
  normal: { label: 'Normal', cls: 'text-emerald-400 border-emerald-800 bg-emerald-950/40', Icon: CheckCircle2 },
  attention: { label: 'Atención', cls: 'text-amber-400 border-amber-800 bg-amber-950/40', Icon: AlertTriangle },
  critical: { label: 'Crítico', cls: 'text-red-400 border-red-800 bg-red-950/40', Icon: XCircle },
  no_metrics: { label: 'Sin métricas', cls: 'text-gray-400 border-gray-700 bg-gray-900', Icon: HelpCircle },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('es-PE') : '—');

const ComponentCard = ({ c }) => {
  const st = STATUS[c.status] || STATUS.no_metrics;
  return (
    <div className={`rounded-xl border p-4 ${st.cls}`} data-testid={`infra-card-${c.component}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-white text-sm font-medium">{c.label}</p>
        <span className="flex items-center gap-1 text-xs font-semibold" data-testid={`infra-status-${c.component}`}>
          <st.Icon className="w-4 h-4" />{st.label}
        </span>
      </div>
      <p className="text-gray-300 text-xs mt-2" data-testid={`infra-cause-${c.component}`}>{c.cause}</p>
      <p className="text-gray-500 text-[11px] mt-2">Última comprobación: {fmt(c.checked_at)}</p>
      {c.incident_open && <p className="text-red-300 text-[11px] mt-1">Incidente abierto desde {fmt(c.incident_opened_at)}</p>}
    </div>
  );
};

export const InfraHealthPanel = () => {
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(false);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (inFlight.current || document.hidden) return; // no duplicate or background-tab requests
    inFlight.current = true;
    try {
      const res = await api.get('/admin/infra/health');
      setData(res.data);
    } catch { /* keep last data */ } finally { inFlight.current = false; }
  }, []);

  const refreshMs = (data?.settings?.dashboard_refresh_seconds || 60) * 1000;
  useEffect(() => {
    load();
    const t = setInterval(load, refreshMs);
    return () => clearInterval(t);
  }, [load, refreshMs]);

  const checkNow = async () => {
    setChecking(true);
    try {
      await api.post('/admin/infra/check-now');
      toast.success('Comprobación completada');
      inFlight.current = false;
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo comprobar');
    } finally { setChecking(false); }
  };

  if (!data) return <div className="text-gray-400 py-10 text-center" data-testid="infra-loading">Cargando…</div>;
  const counts = data.components.reduce((acc, c) => ({ ...acc, [c.status]: (acc[c.status] || 0) + 1 }), {});

  return (
    <div className="space-y-6" data-testid="infra-health-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2 text-xs">
          {Object.entries(STATUS).map(([k, v]) => (
            <span key={k} className={`px-3 py-1 rounded-full border ${v.cls}`} data-testid={`infra-count-${k}`}>{v.label}: {counts[k] || 0}</span>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-500 text-xs">Se actualiza cada {data.settings.dashboard_refresh_seconds} s · {fmt(data.generated_at)}</span>
          <Button size="sm" variant="outline" onClick={checkNow} disabled={checking} className="border-gray-700 text-gray-300" data-testid="infra-check-now-btn">
            {checking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Comprobar ahora
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.components.map((c) => <ComponentCard key={c.component} c={c} />)}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-5">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><BellRing className="w-4 h-4 text-[#FF6B35]" />Últimas alertas enviadas</h3>
          {data.notifications.length === 0 ? <p className="text-gray-500 text-sm">Ninguna.</p> : (
            <ul className="space-y-1 text-sm" data-testid="infra-notifications">
              {data.notifications.map((n, i) => (
                <li key={i} className="flex justify-between gap-3 text-gray-300">
                  <span>{n.kind} · {n.component} · {n.status}{n.ok ? '' : ' (envío fallido)'}</span>
                  <span className="text-gray-500">{fmt(n.sent_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-5">
          <h3 className="text-white font-semibold mb-3">Sin instrumentación (qué falta)</h3>
          <ul className="list-disc pl-5 text-gray-400 text-sm space-y-1" data-testid="infra-not-instrumented">
            {data.not_instrumented.map((t) => <li key={t}>{t}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default InfraHealthPanel;
